import React from "react";
import {
  Card, CardBody, Col, DropdownItem, InputGroup, InputGroupAddon, Row, Input, Button, CustomInput, Badge,
  UncontrolledDropdown, DropdownToggle, DropdownMenu
} from "reactstrap";
import {observer} from "mobx-react-lite";
import {FormattedMessage, useIntl} from "react-intl";
import * as Icon from "react-feather";
import ScanbuyTable, {TableColumn} from "../../tables/ScanbuyTable";
import TableActionsDropdown from "../../tables/TableActionsDropdown";
import dayjs from 'dayjs';
import DeleteMediaFileState from "../delete//DeleteMediaFileState";
import DeleteMediaFileView from "../delete/DeleteMediaFileView";
import GenericModal from "../../elements/modal/GenericModal";
import {useRouterStore} from "mobx-state-router";
import GroupFilterSelect from "../../elements/filters/GroupFilterSelect";
import AdvancedConfFilterSelect from "../../elements/filters/AdvancedConfFilterSelect";
import TagFilterSelect from "../../elements/filters/TagFilterSelect";
import {formConstants} from "../../../utils/constants";
import {buildDimensionFilters} from "../../../utils/buildDimensionFilters";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAngleDoubleDown, faAngleDoubleUp, faSlidersH} from "@fortawesome/free-solid-svg-icons";
import PreviewCodeAndEditDesign from "../../codes/view/PreviewCodeAndEditDesign";
import {MediaFilePreviewState} from "@scanbuy-inc/cmp_v2_pages/mediafile/src/componets/MediaFilePreviewState";
import MediaFilePreview from "@scanbuy-inc/cmp_v2_pages/mediafile/src/componets/MediaFilePreview";
import {MediaFilePreviewWithDownloadState} from "../view/MediaFilePreviewWithDownloadState";
import "./MediaFileListViewCss.css"
import {getFileCategory, isEpsFormat} from "../../../utils/common";
import {createdByColumn, updatedByColumn} from "../../tables/TableColumns";
import OptionalColumnsDropdown from "../../elements/dropdown/OptionalColumnsDropdown";
import ResetButton from "../../ResetButton";
import {buildColumnsWithOptionalSupport} from "../../../utils/optionalColumnsHelper";

export const buildMediaFilesColumns = (routerStore, mediaFilesListViewState) => {
  const {rootStore} = routerStore.options;
  const visible = new Set(mediaFilesListViewState.optionalColumns);

  const c1 = TableColumn.create({name: 'Name', property: 'mediaFile', sortable: true, sortField: 'name'});
  c1.setSelector((row) => row.label);
  const c2 = TableColumn.create({name: 'Usage', property: 'usage'});
  c2.setSelector((row) => row.usage);

  const c3 = TableColumn.create({
    name: 'Format', property: 'fileFormat', sortField: 'fileFormat',
  });
  c3.setSelector((row) => row.fileFormat);
  const cStatus = TableColumn.create({
    name: 'Status', property: 'status', sortable: true, sortField: 'status', selector: 'status', width: '100px',
  })
  cStatus.setSelector((row) => {
    return row.status
  })

  const c4 = TableColumn.create({
    name: 'Tag(s)', property: 'tags'
  });
  const createdBy = createdByColumn();
  const updatedBy = updatedByColumn();
  c4.setSelector((row) => {
    return row.tags;
  });
  c4.setCell((row) => {
    const tags = row.tags.flatMap((t) => {
      return {uuid: t.uuid, name: t.name, color: t.uiStyleConfigs?.color || 'info'}
    });
    return (<div>
      {tags.map((tag, index) => (<Badge
        key={index}
        color="info"
        className="badge-pill mr-1 mb-1"
      >
        {tag.name}
      </Badge>))}
    </div>)
  })

  const c5 = TableColumn.create({
    name: 'Create Date', property: 'created', width: '15%', hide: 'sm', sortable: true, sortField: 'createdAt',
  });
  c5.setSelector((row) => {
    // console.log(row)
    const {createdAt} = row || {};
    const formattedCreateDate = dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss');
    return formattedCreateDate;
  });

  const c6 = TableColumn.create({
    name: 'Last Modified Date', property: 'updated', width: '15%', hide: 'sm', sortable: true, sortField: 'updatedAt',
  });
  c6.setSelector((row) => {
    // console.log(row)
    const {updatedAt} = row || {};
    const formattedUpdateDate = dayjs(updatedAt).format('YYYY-MM-DD HH:mm:ss');
    return formattedUpdateDate;
  });
  const actionColl = TableColumn.create({
    name: 'Actions', property: 'actions', button: true, allowOverflow: true, ignoreRowClick: true
  });
  actionColl.setCell((row) => (
    <TableActionsDropdown actionsList={[
      <DropdownItem key={'edit/' + row.uuid}
                    onClick={() => {
                      routerStore.goTo('editMediaFile', {
                        params: {uuid: row.uuid},
                      });
                    }}
      >
        <Icon.Edit/>&nbsp;Edit</DropdownItem>,
      row.status === 'UPLOADED' ? (
        <DropdownItem key={'download/' + row.uuid}
                      onClick={async () => {
                        await mediaFilesListViewState.downloadFile(row.uuid);
                      }}
        >
          <Icon.Download/>&nbsp;Download
        </DropdownItem>
      ) : null,
      <DropdownItem id={"action-btn-preview"+row} key={'preview' + row.uuid} value={'preview ' + row.uuid}
                    onClick={async () => {
                      // remove old image if it exists
                      mediaFilesListViewState.setBase64NonBrowserConvertedFile("")
                      const imageUrl =  row?.liveLinks?.original
                      const fileFormat = row?.fileFormat
                      // need to convert eps to base64 if it is not supported by the browser
                      if (isEpsFormat({"type": fileFormat})) {
                       await mediaFilesListViewState.fetchEpsAsBase64(imageUrl);
                      }

                      const imageSrc = mediaFilesListViewState.shouldRenderBase64NonBrowserConvertedFile() ?
                        mediaFilesListViewState.base64NonBrowserConvertedFile :
                        row?.liveLinks?.original

                      const mediaFilePreviewState = MediaFilePreviewWithDownloadState.create({
                        fileUrl: imageSrc,
                        fileType: getFileCategory(fileFormat),
                        filename: row?.name || 'download',
                      });

                      mediaFilesListViewState.handleRowClick(row,mediaFilePreviewState,MediaFilePreview,"media-file-preview-modal");
                    }}

        ><Icon.Image/>&nbsp;<FormattedMessage id="listCodes.actions.dropdown.preview.defaultMessage"/>
      </DropdownItem>,
      <DropdownItem key={row.uuid} value={"Delete " + row.uuid} className="align-text-bottom"
                    onClick={() => {
                      const componentStore = DeleteMediaFileState.create(
                        {
                          title: `Delete ${row.name ? row.name : ""}`,
                          uuid: row.uuid
                        });
                      rootStore.rootUiStore.attachPageState('deleteMediaFileStore', componentStore)

                      componentStore.setToggleListener(mediaFilesListViewState.modalStore.toggle)
                      componentStore.setRefreshListener(mediaFilesListViewState.refresh)
                      mediaFilesListViewState.handleRowClick(row, componentStore, DeleteMediaFileView);
                    }}> <Icon.Delete/>&nbsp;<FormattedMessage id="listCodes.actions.dropdown.delete.defaultMessage"/>
      </DropdownItem>
    ]}/>
  ))

  const allColumns = [c1, cStatus, c4, createdBy, updatedBy, c3, c2, c5, c6, actionColl];

  return buildColumnsWithOptionalSupport(allColumns, mediaFilesListViewState, rootStore);
}

const MediaFilesListView = observer(({mediaFilesListViewState}) =>{
        const routerStore = useRouterStore();
        const intl = useIntl();
        //TODO: PATTERN VIOLATION - setOnSelectChanges called on every render
        // See correct pattern: setOnSelectChanges() in src/routes/codes/index.js onEnter
        mediaFilesListViewState.setOnSelectChanges()
        //TODO: PATTERN VIOLATION - Multiple filter setters called on every render (lines 174-182)
        // See correct pattern: setOnSelectChanges() in src/routes/codes/index.js onEnter
        mediaFilesListViewState.listModel.store.setFilterOn(mediaFilesListViewState.groupSelectStore?.selectedGroup?.uuid)
        mediaFilesListViewState?.advFilterSelectState?.setEmails(mediaFilesListViewState.listModel?.filters?.users);
        mediaFilesListViewState?.advFilterSelectState?.setStatusTypes(mediaFilesListViewState.listModel?.filters?.status);
        mediaFilesListViewState?.advFilterSelectState?.setUsage(mediaFilesListViewState.listModel?.filters?.content);
        mediaFilesListViewState?.advFilterSelectState?.setFileFormat(mediaFilesListViewState.listModel?.filters?.fileFormat);
        mediaFilesListViewState?.tagFilterSelectState?.setTags(mediaFilesListViewState.listModel?.filters?.tags);
        mediaFilesListViewState?.filterGroup && mediaFilesListViewState?.groupFilterSelectState?.setGroups(mediaFilesListViewState.listModel?.filters?.groups);
        mediaFilesListViewState?.dimensionSelectViewState?.setFilteredDimensions(mediaFilesListViewState?.listModel?.filters.dimensions);
        mediaFilesListViewState?.loadFiltersFromSessionStorage();
        const hasDimensions =
          !!mediaFilesListViewState?.dimensionSelectViewState?.dimensionsEnabled &&
          (mediaFilesListViewState?.dimensionSelectViewState?.dimensionSelectStates?.length ?? 0) > 0;


  const customStyles = {
    headRow: {
      style: {
        border: 'none',
      },
    },
    headCells: {
      style: {
        color: '#202124',
        fontSize: '14px',
      },
    },
    rows: {
      highlightOnHoverStyle: {
        backgroundColor: 'rgb(230, 244, 244)',
        borderBottomColor: '#FFFFFF',
// borderRadius: '5px',
        outline: '1px solid #FFFFFF',
      },
    },
    pagination: {
      style: {
        border: 'none',
      },
    },
  }
    // mediaFilesListViewState.ifModalIsClosed()
  return (
    <>
      <Card>
        <CardBody>
          <Row className="mb-2">
            <Col className="d-flex justify-content-end list-view-actions-container">
              {mediaFilesListViewState.isFilterVisible ? (
                <Button
                  color="primary"
                  onClick={() => mediaFilesListViewState.toggleFilterVisible()}
                >
                  <FontAwesomeIcon icon={faAngleDoubleUp} />
                </Button>
              ) : (
                <Button
                  color="primary"
                  onClick={() => mediaFilesListViewState.toggleFilterVisible()}
                >
                  <FontAwesomeIcon icon={faAngleDoubleDown} />
                </Button>
              )}

              <OptionalColumnsDropdown
                listViewState={mediaFilesListViewState}
                buildColumnsFunction={buildMediaFilesColumns}
                idPrefix="mediafile-col"
              />
            </Col>
          </Row>

 {mediaFilesListViewState?.isFilterVisible && <>
              <Row>
                <Col sm={5}>
                  <InputGroup className="mb-3">
                    <InputGroupAddon addonType="prepend">
                      {intl.formatMessage({id: "filterByGroups.defaultMessage"})}
                    </InputGroupAddon>
                    <GroupFilterSelect groupFilterSelectState={mediaFilesListViewState.groupFilterSelectState}/>
                  </InputGroup>
                </Col>
                <Col sm={5}>
                  <InputGroup className="mb-3">
                    <InputGroupAddon addonType="prepend">
                      {intl.formatMessage({id: "listCodes.filter.filterName.defaultMessage"})}
                    </InputGroupAddon>
                    <AdvancedConfFilterSelect advFilterSelectState={mediaFilesListViewState.advFilterSelectState}/>
                  </InputGroup>
                </Col>
                {!hasDimensions && <ResetButton viewState={mediaFilesListViewState}/>}
              </Row>
              <Row className="list-view-row-align-end">
                <Col sm={10}>
                  <Row>
                    {mediaFilesListViewState?.dimensionSelectViewState?.dimensionsEnabled && mediaFilesListViewState?.dimensionSelectViewState?.dimensionSelectStates?.map(dimensionSelectStore => buildDimensionFilters(dimensionSelectStore,6))}
                  </Row>
                </Col>
                {hasDimensions && <ResetButton viewState={mediaFilesListViewState}/>}
              </Row>
              <Row>
                <Col sm={5}>
                  <InputGroup className="mb-3">
                    <InputGroupAddon addonType="prepend">
                      {intl.formatMessage({id: "listCodes.searchInput.defaultMessage"})}
                    </InputGroupAddon>
                    <Input
                      type="text"
                      name="searchInput"
                      placeholder="Search"
                      onChange={mediaFilesListViewState.onSearchChange}
                    />
                  </InputGroup>
                </Col>
                <Col sm={5}>
                  <InputGroup className="mb-3">
                    <InputGroupAddon addonType="prepend">
                      {intl.formatMessage({id: "listCodes.filterBytags.defaultMessage"})}
                    </InputGroupAddon>
                    <TagFilterSelect tagFilterSelectState={mediaFilesListViewState.tagFilterSelectState}/>
                  </InputGroup>
                </Col>
                <Col sm={1}>
                  <CustomInput
                    type="switch"
                    id="isFilterTypeOR"
                    name="isFilterTypeOR"
                    label={formConstants.Mode.OR}
                    checked={mediaFilesListViewState.isFilterTypeOR}
                    onChange={() => mediaFilesListViewState.selectFilterMethod()}
                  />
                </Col>
                <Col sm={1} className="justify-content-end text-right">
                  <Button color="primary" onClick={() => {
                    routerStore.goTo('createMediaFile', {});
                  }}>
                    <FormattedMessage id="qrDesign.manageList.create.defaultMessage"/>
                  </Button>
                </Col>
              </Row>
            </>}


          <Row>
            <Col>
            {mediaFilesListViewState.listModel.store.list.length ?
            <ScanbuyTable
              key={mediaFilesListViewState?.columns.length}
              columns={mediaFilesListViewState.columns}
              customStyles={customStyles}
              store={mediaFilesListViewState.listModel}
            />
              :
              <ScanbuyTable
                      key={mediaFilesListViewState?.columns.length}
                      columns={mediaFilesListViewState.columns}
                      customStyles={customStyles}
                      store={mediaFilesListViewState.listModel}
              />
            }
            </Col>
          </Row>
        </CardBody>
      </Card>
        <GenericModal
                modalStore={mediaFilesListViewState.modalStore}
                componentStore={mediaFilesListViewState.modalComponentStore}
                EmbedComponent={mediaFilesListViewState.modalComponent}
                className={mediaFilesListViewState.modalClassName}
                headerActions={mediaFilesListViewState.modalComponentStore?.downloadFile && mediaFilesListViewState.modalComponentStore?.fileUrl ? (
                  <Button color="secondary" size="sm" onClick={() => mediaFilesListViewState.modalComponentStore.downloadFile()}>
                    <Icon.Download size={14}/>&nbsp;<FormattedMessage id="mediaFile.download.defaultMessage" defaultMessage="Download"/>
                  </Button>
                ) : null}
        />
    </>
  )
}
)

export default MediaFilesListView
